/**
 * The rules the test-clock screens draw by, kept out of the components so they can be tested
 * without mounting one.
 *
 * A clock is advanced to an **absolute** instant — the provider takes a time, not a duration — so
 * every quick step is worked out here from the clock's own frozen time, never from the admin's
 * wall clock: a clock already a month ahead advanced "one day" must land a day after *it*.
 */

import type { TestClock, TestClockStatus } from '../AppTypes';

/** One of the quick steps offered beside a clock. */
export interface AdvanceStep {
	key: 'hour' | 'day' | 'week' | 'month' | 'year';
	label: string;
}

/**
 * Hour and day for a daily price and a dunning retry, week for a trial, month and year for a
 * renewal. The provider refuses a jump of more than two billing intervals of the shortest
 * subscription on the clock, and says so in a sentence the screen shows.
 */
export const ADVANCE_STEPS: readonly AdvanceStep[] = [
	{ key: 'hour', label: '+1 hour' },
	{ key: 'day', label: '+1 day' },
	{ key: 'week', label: '+1 week' },
	{ key: 'month', label: '+1 month' },
	{ key: 'year', label: '+1 year' }
];

const HOUR_MS = 60 * 60 * 1000;

/**
 * `frozenTime` moved on by one step, as an ISO string.
 *
 * Months and years are **calendar** steps in UTC, clamped to the end of a shorter month — 31
 * January plus a month is 28 (or 29) February, not 3 March. A renewal falls on the same calendar
 * day, so a step that drifted into the next month would carry the clock past the renewal it was
 * meant to land on and through the one after it.
 */
export function advancedBy(frozenTime: string, step: AdvanceStep['key']): string {
	const from = new Date(frozenTime);
	switch (step) {
		case 'hour':
			return new Date(from.getTime() + HOUR_MS).toISOString();
		case 'day':
			return new Date(from.getTime() + 24 * HOUR_MS).toISOString();
		case 'week':
			return new Date(from.getTime() + 7 * 24 * HOUR_MS).toISOString();
		case 'month':
			return addCalendarMonths(from, 1).toISOString();
		case 'year':
			return addCalendarMonths(from, 12).toISOString();
	}
}

function addCalendarMonths(from: Date, months: number): Date {
	const target = new Date(from.getTime());
	const day = from.getUTCDate();
	target.setUTCDate(1);
	target.setUTCMonth(target.getUTCMonth() + months);
	const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
	target.setUTCDate(Math.min(day, lastDay));
	return target;
}

/**
 * An ISO instant as the value a `datetime-local` input holds, in the browser's own zone — the input
 * has no zone of its own, so it is filled and read in local time and converted at the edges.
 */
export function toLocalInputValue(iso: string): string {
	const date = new Date(iso);
	const pad = (value: number): string => String(value).padStart(2, '0');
	return (
		`${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
		`T${pad(date.getHours())}:${pad(date.getMinutes())}`
	);
}

/** A `datetime-local` value back to an ISO instant, or null when the field is empty or unreadable. */
export function fromLocalInputValue(value: string): string | null {
	if (value.trim() === '') {
		return null;
	}
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** A clock's time written out in full — a test is about which day it is, and what time on it. */
export function formatClockTime(iso: string): string {
	return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

const STATUS_LABEL: Record<TestClockStatus, string> = {
	ready: 'Ready',
	advancing: 'Advancing…',
	internal_failure: 'Failed at the provider'
};

export function clockStatusLabel(status: TestClockStatus): string {
	return STATUS_LABEL[status] ?? status;
}

export function clockStatusTone(status: TestClockStatus): 'green' | 'yellow' | 'red' {
	return status === 'ready' ? 'green' : status === 'advancing' ? 'yellow' : 'red';
}

/** Whether a screen showing these clocks should look again shortly, because one is still moving. */
export function anyClockAdvancing(clocks: readonly (TestClock | null)[]): boolean {
	return clocks.some(clock => clock?.status === 'advancing');
}

/** How often a moving clock is re-read. Stripe usually settles in a few seconds. */
export const CLOCK_POLL_MS = 2000;
